import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  catchError,
  debounceTime,
  first,
  map,
  pairwise,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import {
  ReferenceEntity,
  ReferenceObligation,
} from 'src/app/static-data/models/interfaces/static-data';

import {
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NgbDate, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as dayjs from 'dayjs';
import { EMPTY, Observable, Subject, forkJoin, merge, of } from 'rxjs';
import { AuthService } from 'src/app/auth-o3/shared/services/auth.service';
import { DcAlertService } from 'src/app/ui-kit/dc-alert/dc-alert.service';
import { DcInputNumberPercentIndicativeFinalService } from 'src/app/ui-kit/dc-input-number-percent-indicative-final/dc-input-number-percent-indicative-final.service';
import { DateUtils } from 'src/app/utils/date.utils';
import { FormConfigService } from '../../../form-config/form-config.service';
import { Records } from '../../../form-config/model/records.type';
import { KeyDateDetail } from '../../model/classes/key-date-detail';
import { FREQUENCY } from '../../model/consts/frequency.const';
import { PERFORMANCE_TYPES_REFERENCE } from '../../model/consts/performance-type-reference-modal.const';
import {
  PERFORMANCE_TYPES,
  PERFORMANCE_TYPE_KEYS,
} from '../../model/consts/performance-type.const';
import { SETTLEMENT_TYPE_REFERENCE } from '../../model/consts/settlement-type-reference-modal.const';
import { SETTLEMENT_TYPE } from '../../model/consts/settlement-type.const';
import {
  ProductRecordStatus,
  STATUS_TYPE,
} from '../../model/consts/status-type.const';
import { PRIORITY_ENUM } from '../../model/enums/priority.enum';
import {
  KeyDatesEnum,
  PayoffEnum,
  ProductCaptureGroupsEnum,
  ProductTypeEnum,
  SummaryEnum,
} from '../../model/enums/product-capture.enums';
import { Currency } from '../../model/interfaces/currency.interface';
import { CalendarType } from '../../model/interfaces/trading-calendar.interface';
import { UnderlyingAsset } from '../../model/interfaces/underlying-asset.interface';
import { ProductCaptureService } from '../../services/product-capture.service';
import { RecordWorkflowService } from '../../services/record-workflow.service';
import { DateDiffEvaluator } from '../../utils/day-diff-evaluator';
import { GetDateObjectFromString } from '../../utils/get-date-object-from-string';
import { GetTenorPeriodFromArray } from '../../utils/get-tenor-period-from-array';
import { GetTenorUnitFromArray } from '../../utils/get-tenor-unit-from-array';
import { objectPropertiesAllNull } from '../../utils/object-properties-all-null-checker';
import { TenorSplitter } from '../../utils/tenor-splitter';
import { CreditProvisionService } from '../credit-provision/credit-provision.service';
import { KeyDatesModalService } from './key-dates-modal/key-dates-modal.service';
import { KeyDate } from './key-dates-modal/key-dates-type.const';
import { KeyDatesSnapshotService } from './key-dates-snapshot.service';
import { validateIsin } from './product-summary.validators';
import { RecordWorkflowModalService } from './record-workflow-modal/record-workflow-modal.service';
import { ReferenceModalService } from './reference-modal/reference-modal.service';
import { UnderlyingAssetsModalService } from './underlying-assets-modal/underlying-assets-modal.service';
import { ProductUtils } from '../../utils/product.utils';
import { RestrictIfService } from '../../../auth-o3/shared/restrict/restrict-if.service';

@Component({
  selector: 'app-product-summary',
  templateUrl: './product-summary.component.html',
  styleUrls: ['./product-summary.component.scss'],
})
export class ProductSummaryComponent implements OnInit, OnDestroy {
  @Input()
  formName: string;

  @Input()
  productId: number;

  @Input()
  productTypeValue: ProductTypeEnum;

  @Input()
  canUserEdit = false;

  formGroupName = ProductCaptureGroupsEnum.summary;
  currencies: Currency[] = [];
  currencyItems: Record<string, any>[];
  form: UntypedFormGroup;
  frequencyItems = FREQUENCY;
  labels: Record<string, any>;
  performanceTypeItems = PERFORMANCE_TYPES;
  productTypeItems: Records;
  productTypes = ProductTypeEnum;
  settlementTypeItems = SETTLEMENT_TYPE;
  statusItems: Observable<{ id: ProductRecordStatus; displayValue: string }[]>;
  underlyingAssets: UnderlyingAsset[];
  underlyingAssetsLabel: string;

  initialRecordStatus: string = null;

  tradingCalendarItemsClean: KeyDate[];
  settlementCalendarItemsClean: KeyDate[];

  tradingCalendarItemsUse: KeyDate[] = [];
  initCurrency: string;
  referenceObligationItems: ReferenceObligation[] = [];
  referenceEntityItems: ReferenceEntity[] = [];
  destroy$ = new Subject<boolean>();

  showProductFinalisationDateRow: boolean;

  private isEditing = false;

  constructor(
    private formConfigService: FormConfigService,
    public rootFormGroup: FormGroupDirective,
    private productCaptureService: ProductCaptureService,
    private underlyingAssetsModalService: UnderlyingAssetsModalService,
    private keyDatesModalService: KeyDatesModalService,
    private referenceModalService: ReferenceModalService,
    public ngbModal: NgbModal,
    private creditProvisionService: CreditProvisionService,
    private dcInputNumberPercentIndicativeFinalService: DcInputNumberPercentIndicativeFinalService,
    private recordWorkflowService: RecordWorkflowService,
    private recordWorkflowModalService: RecordWorkflowModalService,
    private dcAlertService: DcAlertService,
    private keyDatesSnapshotService: KeyDatesSnapshotService,
    private restrictIfService: RestrictIfService,
    private authService: AuthService,
    router: Router
  ) {
    this.isEditing = router.url.includes('edit-product');
  }

  ngOnInit(): void {
    this.labels = this.formConfigService.getLabels();

    this.underlyingAssetsLabel =
      this.labels.summary.underlyingAssetsGroup.underlyingAssetsGroup;

    this.getReferenceOptions();
    this.getCurrencies();
    this.form = this.rootFormGroup.control.get(
      this.formGroupName
    ) as UntypedFormGroup;
    this.initCurrency = this.currencyField.value;

    this.addAsyncValidators();

    this.initialRecordStatus = this.form.get('recordStatus')?.value;
    this.populateStatusItems();

    this.dcInputNumberPercentIndicativeFinalService.updates$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vals) => {
        this.showProductFinalisationDateRow =
          Object.values(vals).includes(true);

        // sets defaults for Proudct Finalisation Date
        if (
          this.showProductFinalisationDateRow &&
          objectPropertiesAllNull(this.productFinalisationDate.value)
        ) {
          this.productFinalisationDate.get('priority').setValue('relative');
          this.productFinalisationDate.get('value').setValue(null);
          this.productFinalisationDate.get('anchorDate').setValue('ISSUE_DATE');
          this.productFinalisationDate.get('offset').setValue('0B');
          this.productFinalisationDate.get('calendar').setValue('SETTLEMENT');
          this.productFinalisationDate
            .get('rollConvention')
            .setValue('PRECEDING');
        }

        // resets Product Finalisation Date properties
        if (
          !this.showProductFinalisationDateRow &&
          !objectPropertiesAllNull(this.productFinalisationDate.value)
        ) {
          this.productFinalisationDate.get('priority').setValue(null);
          this.productFinalisationDate.get('value').setValue(null);
          this.productFinalisationDate.get('anchorDate').setValue(null);
          this.productFinalisationDate.get('offset').setValue(null);
          this.productFinalisationDate.get('calendar').setValue(null);
          this.productFinalisationDate.get('rollConvention').setValue(null);
        }
      });

    this.setProductTypes();

    this.setPerformanceAndSettlementDropdownOptions(
      this.form.get(SummaryEnum.productType).value
    );

    this.underlyingAssetsLabel = this.getUnderlyingAssetLabel(
      this.productTypeValue
    );
    this.form
      .get(SummaryEnum.productType)
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.productTypeValue = val;
        this.setPerformanceAndSettlementDropdownOptions(val);

        this.underlyingAssetsLabel = this.getUnderlyingAssetLabel(val);
      });

    forkJoin([
      this.productCaptureService.getCalendarsAsDropdownOptions(
        CalendarType.TRADING
      ),
      this.productCaptureService.getCalendarsAsDropdownOptions(
        CalendarType.SETTLEMENT
      ),
    ]).subscribe(([trading, settlement]) => {
      this.tradingCalendarItemsClean = trading;
      this.settlementCalendarItemsClean = settlement;
    });

    this.currencyField.valueChanges
      .pipe(startWith(null as string), pairwise(), takeUntil(this.destroy$))
      .subscribe(([prev, next]) => {
        if (prev !== next) {
          const prevCurrency = this.currencies.find(
            (c) => c.shortName === (prev ? prev : this.initCurrency)
            // this is a workaround because load value at init
            // isn't being emitted by the dc-select component because the user didn't
            // change the value
          );
          let newCalendar: string[] = this.settlementCalendars.value;

          prevCurrency?.calendars?.forEach((calendar) => {
            newCalendar = newCalendar?.filter((c) => c !== calendar);
          });
          const settlementCalendar =
            this.productCaptureService.setSettlementCalendar(next, newCalendar);

          this.settlementCalendars.setValue(settlementCalendar);
        }
      });

    merge(
      this.redemptionDateField.valueChanges.pipe(
        switchMap((val) => (val ? of(this.redemptionDatePriority) : EMPTY)),
        debounceTime(250)
      ),
      this.issueDateField.valueChanges.pipe(
        switchMap((val) => (val ? of(this.issueDatePriority) : EMPTY)),
        debounceTime(250)
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((control: UntypedFormControl) => {
        control.setValue(PRIORITY_ENUM.ABSOLUTE);
        this.keyDatesModalService.updateKeyDatesValuesNoModal(
          this.prepareKeyDatesData()
        );
      });

    merge(
      this.tenorField.valueChanges,
      this.issueDateField.valueChanges,
      this.redemptionDateField.valueChanges
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.tenorIssueRedemptionSetter(
          this.tenorField,
          this.issueDateField,
          this.redemptionDateField
        );
      });

    const preparedKeyDates = this.prepareKeyDatesData();
    this.keyDatesModalService.updateKeyDatesValuesNoModal(preparedKeyDates);

    if (this.isEditing) {
      this.keyDatesSnapshotService.recordSnapshot({
        ...preparedKeyDates,
        tenor: preparedKeyDates.tenorField,
      });
    } else {
      this.keyDatesSnapshotService.resetSnapshot();
    }

    this.couponTypeControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(this.couponTypeControl.value),
        pairwise()
      )
      .subscribe(([_, next]) => {
        if (next === 'NONE') {
          const result = {
            value: null,
            priority: null,
            calendar: null,
            rollConvention: null,
            offset: null,
            anchorDate: null,
          };

          this.setupInterestDate(KeyDatesEnum.interestStartDate, result);
          this.setupInterestDate(KeyDatesEnum.interestEndDate, result);
        }
      });
  }

  private setProductTypes(): void {
    this.productTypeItems = this.formConfigService.getOptionsByFieldName(
      'productType',
      this.formName
    );

    this.authService.getUserTenant()
      .pipe(
        first()
      ).subscribe((tenant) => {
        if (tenant === 'DeutscheBank' && this.canUserEdit) {
          this.productTypeItems = this.productTypeItems.filter((item) => item.id === 'CLN');
        }
      });
  }

  private getUnderlyingAssetLabel(productType: string): string {
    return ProductUtils.isFxPair(productType as ProductTypeEnum)
      ? this.labels.summary.underlyingAssetsGroup.fxPairs
      : this.labels.summary.underlyingAssetsGroup.underlyingAssetsGroup;
  }

  private populateStatusItems(): void {
    if (!this.isEditing) {
      this.statusItems = of(
        STATUS_TYPE.filter(
          (status) => status.id === 'Draft' || status.id === 'In Review'
        )
      );

      return;
    }

    this.statusItems = forkJoin([
      this.restrictIfService.hasRoles(
        'V2_ADMINISTRATOR',
        'V2_LIFECYCLE_MANAGEMENT_SUPERVISOR',
        'V2_NEW_ISSUANCE_TRANSACTION_MANAGEMENT_SUPERVISOR'
      ),
      this.recordWorkflowService.getNextWorkflowStates(
        this.form.get('recordStatus')?.value
      ),
    ]).pipe(
      map(this.filterStatusBySupervisorRole),
      map((nextStateDisplayNames: string[]) => {
        return STATUS_TYPE.filter((status) =>
          nextStateDisplayNames.includes(status.displayValue)
        ).concat(
          STATUS_TYPE.find(
            (status) => status.id === this.initialRecordStatus
          ) || []
        );
      }),
      catchError((err) => {
        this.dcAlertService.showAlert({
          type: 'danger',
          message:
            'Something went wrong while fetching the available statuses for the product',
        });
        console.error(err);
        return of([]);
      }),
      first()
    );
  }

  openRecordWorkflowIfRequired(newRecordStatus: any): void {
    if (!newRecordStatus) {
      return;
    }

    if (
      (this.initialRecordStatus === 'In Review' &&
        newRecordStatus.id !== 'In Review') ||
      newRecordStatus.id === 'Deleted'
    ) {
      this.recordWorkflowModalService.openDialog({
        currentRecordStatus: this.initialRecordStatus,
        newRecordStatus,
        recordTransitionReason: this.form.get('recordStatusReason')?.value,
        onSave: (reason: string) => {
          this.form.get('recordStatusReason')?.setValue(reason);
          this.form.get('recordStatus')?.setValue(newRecordStatus.id);
        },
        onCancel: () => {
          this.form.get('recordStatus')?.setValue(this.initialRecordStatus);
        },
      });

      return;
    }

    this.form.get('recordStatus')?.setValue(newRecordStatus.id);
  }

  private tenorIssueRedemptionSetter(
    tenor: UntypedFormControl,
    issueDate: UntypedFormControl,
    redemptionDate: UntypedFormControl
  ): void {
    const tenorValue: string = tenor.value;
    const tenorPristine: boolean = tenor.pristine;
    const issueDateValue: NgbDate = issueDate.value;
    const issueDatePristine: boolean = issueDate.pristine;
    const redemptionDateValue: NgbDate = redemptionDate.value;
    const redemptionDatePristine: boolean = redemptionDate.pristine;

    if (
      tenorValue &&
      issueDateValue &&
      !redemptionDateValue &&
      redemptionDatePristine
    ) {
      const redemptionDate = this.setDate(tenorValue, issueDateValue, 'ISSUE');
      if (redemptionDate) {
        this.redemptionDateField.setValue(redemptionDate);
        this.redemptionDateField.markAsTouched();
        this.redemptionDateField.updateValueAndValidity();
      }
    }
    if (
      tenorValue &&
      !issueDateValue &&
      issueDatePristine &&
      redemptionDateValue
    ) {
      const issueDate = this.setDate(
        tenorValue,
        redemptionDateValue,
        'REDEMPTION'
      );
      if (issueDate) {
        this.issueDateField.setValue(issueDate);
        this.issueDateField.markAsTouched();
        this.issueDateField.updateValueAndValidity();
      }
    }
    if (!tenorValue && tenorPristine && redemptionDateValue && issueDateValue) {
      const tenor = this.setTenor(issueDateValue, redemptionDateValue);
      if (tenor) {
        this.tenorField.setValue(tenor);
        this.tenorField.markAsTouched();
        this.tenorField.updateValueAndValidity();
      }
    }
  }

  private setDate(
    tenor: string,
    dateValue: NgbDate,
    dateType: 'ISSUE' | 'REDEMPTION'
  ): NgbDateStruct {
    const dateString = DateUtils.getStringFromDateObject(dateValue);
    const tenorArr = TenorSplitter(tenor);
    const tenorUnit = GetTenorUnitFromArray(tenorArr);
    const tenorPeriod = GetTenorPeriodFromArray(tenorArr);

    if (dateType === 'ISSUE' && tenorArr) {
      const redemptionDateStr = dayjs(dateString)
        .add(tenorUnit, tenorPeriod)
        .format('YYYY-MM-DD');

      const redemptionDateObj = GetDateObjectFromString(redemptionDateStr);
      return redemptionDateObj;
    }

    if (dateType === 'REDEMPTION' && tenorArr) {
      const issueDateStr = dayjs(dateString)
        .subtract(tenorUnit, tenorPeriod)
        .format('YYYY-MM-DD');

      const issueDateObj = GetDateObjectFromString(issueDateStr);
      return issueDateObj;
    }
    return;
  }

  private setTenor(issueDate: NgbDate, redemptionDate: NgbDate): string {
    const issueDateStr = DateUtils.getStringFromDateObject(issueDate);
    const redemptionDateStr = DateUtils.getStringFromDateObject(redemptionDate);

    const diffInDays = DateDiffEvaluator(
      issueDateStr,
      redemptionDateStr,
      'day'
    );

    const diffInWeeks = DateDiffEvaluator(
      issueDateStr,
      redemptionDateStr,
      'week'
    );

    const diffInMonths = DateDiffEvaluator(
      issueDateStr,
      redemptionDateStr,
      'month'
    );

    const diffInYears = DateDiffEvaluator(
      issueDateStr,
      redemptionDateStr,
      'year'
    );

    if (diffInDays > 7 && diffInMonths < 1) {
      return `${Math.round(diffInWeeks)}W`;
    }

    if (diffInMonths < 1 && diffInMonths > -1) {
      return `${diffInDays}D`;
    }

    return Math.round(diffInMonths) % 12 === 0
      ? `${Math.round(diffInYears)}Y`
      : `${Math.round(diffInMonths)}M`;
  }

  addAsyncValidators(): void {
    this.ISINField.setAsyncValidators(this.validateNameAndIsin('isin'));
  }

  validateNameAndIsin(
    field: string
  ): (group: UntypedFormControl) => Observable<ValidationErrors> {
    return validateIsin(this.productCaptureService, field);
  }

  hasAssetIndexType(): boolean {
    return this.underlyingAssetsArray.value?.some((asset) => {
      const isIndex = asset.type === 'INDEX';
      return isIndex;
    });
  }

  isSettlementTypePhysical(): boolean {
    return this.settlementType.value === 'PHYSICAL';
  }

  getISINErrorMessage(): string {
    if (this.ISINField.errors?.notUnique) {
      return 'Another product already has this ISIN';
    }

    return 'Please use correct ISIN formatting';
  }

  isSettlementWarning(): boolean {
    return this.isSettlementTypePhysical() && this.hasAssetIndexType();
  }

  updateSelectedReferenceEntities(): void {
    const selectedReferenceEntities: ReferenceEntity[] = [];
    this.referenceEntitiesDisplay?.value?.forEach((entityId: number) => {
      const entity = this.referenceEntityItems.find(
        (entity: ReferenceEntity) => entity.referenceEntityID === entityId
      );

      if (entity) {
        selectedReferenceEntities.push(entity);
      }
    });

    this.selectedReferenceEntities$.next(selectedReferenceEntities);
  }

  private getCurrencies(): void {
    this.productCaptureService.getCurrencies().subscribe((currencies) => {
      if (currencies?.length > 0 && !this.currencyItems?.length) {
        this.currencies = currencies;

        this.currencyItems = currencies.map((currency) => {
          return {
            id: currency.shortName,
            displayValue: currency.shortName,
          };
        });
      }
    });
  }

  openUnderlyingAssetsModal(): void {
    this.underlyingAssetsModalService
      .openDialog({
        productTypeValue: this.productTypeValue,
        productCurrency: this.currencyField.value,

        performanceType: this.performanceType.value,
        settlementType: this.settlementType.value,
        underlyingAssets: this.underlyingAssetsArray.value,
        rollAs: this.rollAs.value,
        legalForm: this.rootFormGroup.form.get('legalForm'),

        tradingCalendarsArrayAtOpen: this.tradingCalendars.value,
        underlyingAssetsAtOpen: this.underlyingAssetsArray.value,
      })
      ?.then(
        (result) => {
          if (result !== 'cancel') {
            const {
              performanceType,
              settlementType,
              underlyingAssets,
              rollAs,

              tradingCalendarsArrayAtOpen,
              underlyingAssetsAtOpen,
            } = result;

            this.performanceType.setValue(performanceType);
            this.settlementType.setValue(settlementType);
            this.underlyingAssetsArray.setValue(underlyingAssets);
            this.rollAs.setValue(rollAs);

            this.updateTradingCalendarArray(
              tradingCalendarsArrayAtOpen,
              underlyingAssetsAtOpen,
              underlyingAssets
            );

            const underlyingCalendarsOut =
              this.productCaptureService.getAllUnderlingAssetCalendars(
                underlyingAssets
              );

            this.tradingCalendarItemsUse =
              this.productCaptureService.updateCalenderItemDisabledStatus(
                this.tradingCalendarItemsClean,
                underlyingCalendarsOut
              );
          }
        },
        (error) => {
          console.log(error);
        }
      );
  }

  updateTradingCalendarArray(
    tradingCalendarArrayIn: string[],
    underlyingAssetsArrayIn: UnderlyingAsset[],
    underlyingAssetsArrayOut: UnderlyingAsset[]
  ): void {
    const tradingCalendarsIn = tradingCalendarArrayIn || [];
    const underlyingCalendarsIn =
      this.productCaptureService.getAllUnderlingAssetCalendars(
        underlyingAssetsArrayIn
      );
    const underlyingCalendarsOut =
      this.productCaptureService.getAllUnderlingAssetCalendars(
        underlyingAssetsArrayOut
      );

    if (underlyingCalendarsIn.length > underlyingCalendarsOut.length) {
      const newTradingCals = this.setCalendarsAfterRemoval(
        underlyingCalendarsIn,
        underlyingCalendarsOut,
        tradingCalendarsIn
      );
      this.tradingCalendars.setValue(newTradingCals);
    }

    if (underlyingCalendarsIn.length < underlyingCalendarsOut.length) {
      const updatedTradingCalsUnique = this.setCalendarsAfterAddition(
        underlyingCalendarsIn,
        underlyingCalendarsOut,
        tradingCalendarsIn
      );
      this.tradingCalendars.setValue(updatedTradingCalsUnique);
    }

    if (underlyingCalendarsIn.length === underlyingCalendarsOut.length) {
      const newTradingCals = this.setCalendarsWhenNoIncreaseInCals(
        underlyingCalendarsIn,
        underlyingCalendarsOut,
        tradingCalendarsIn
      );
      if (newTradingCals) {
        this.tradingCalendars.setValue(newTradingCals);
      }
    }
  }

  setCalendarsAfterRemoval(
    underlyingCalendarsIn: string[],
    underlyingCalendarsOut: string[],
    tradingCalendarsIn: string[]
  ): string[] {
    const removedCals = this.removedCalendarFinder(
      underlyingCalendarsIn,
      underlyingCalendarsOut
    );

    const newTradingCals = this.addedCalendarFinder(
      removedCals,
      tradingCalendarsIn
    );

    return newTradingCals;
  }

  setCalendarsAfterAddition(
    underlyingCalendarsIn: string[],
    underlyingCalendarsOut: string[],
    tradingCalendarsIn: string[]
  ): string[] {
    const addedCals = this.addedCalendarFinder(
      underlyingCalendarsIn,
      underlyingCalendarsOut
    );

    const updatedTradingCals = [...tradingCalendarsIn, ...addedCals];
    const updatedTradingCalsUnique = [...new Set(updatedTradingCals)];
    return updatedTradingCalsUnique;
  }

  setCalendarsWhenNoIncreaseInCals(
    underlyingCalendarsIn: string[],
    underlyingCalendarsOut: string[],
    tradingCalendarsIn: string[]
  ): string[] | void {
    const changeDetector =
      underlyingCalendarsIn.sort().toString().toUpperCase() ===
      underlyingCalendarsOut.sort().toString().toUpperCase();

    if (!changeDetector) {
      const calsToRemove = this.removedCalendarFinder(
        underlyingCalendarsIn,
        underlyingCalendarsOut
      );

      const tradingCalsWithRemoval = this.addedCalendarFinder(
        calsToRemove,
        tradingCalendarsIn
      );

      const addToTradingCals = [
        ...tradingCalsWithRemoval,
        ...underlyingCalendarsOut,
      ];
      const newTradingCalsUnique = [...new Set(addToTradingCals)];
      return newTradingCalsUnique;
    }
  }

  removedCalendarFinder = (inArr: string[], outArr: string[]) =>
    inArr.filter((cal) => !outArr.includes(cal));

  addedCalendarFinder = (inArr: string[], outArr: string[]) =>
    outArr.filter((cal) => !inArr.includes(cal));

  createTradingCalendarItemsOnInit(underlying, cleanItems): KeyDate[] {
    const UA =
      this.productCaptureService.getAllUnderlingAssetCalendars(underlying);

    const items = this.productCaptureService.updateCalenderItemDisabledStatus(
      cleanItems,
      UA
    );

    return items;
  }

  openKeyDatesModal(): void {
    if (!this.tradingCalendarItemsUse.length) {
      this.tradingCalendarItemsUse = this.createTradingCalendarItemsOnInit(
        this.underlyingAssetsArray.value,
        this.tradingCalendarItemsClean
      );
    }

    this.keyDatesModalService.open(this.prepareKeyDatesData()).then(
      (result) => {
        if (result !== 'cancel') {
          this.tenorField.setValue(result.tenorField);
          this.frequencyField.setValue(result.frequencyField);
          this.tradingCalendars.setValue(result.tradingCalendars);
          this.settlementCalendars.setValue(result.settlementCalendars);
          this.tradingRollConvention.setValue(result.tradingRollConvention);
          this.accrualRollConvention.setValue(result.accrualRollConvention);
          this.settlementRollConvention.setValue(
            result.settlementRollConvention
          );

          this.issueDateField.setValue(result.issueDateField);
          this.issueDatePriority.setValue(result.issueDatePriority);
          this.issueDateAnchorDate.setValue(result.issueDateAnchorDate);
          this.issueDateOffset.setValue(result.issueDateOffset);
          this.issueDateCalendar.setValue(result.issueDateCalendar);
          this.issueDateRollConvention.setValue(result.issueDateRollConvention);

          this.setupInterestDate(KeyDatesEnum.interestStartDate, result);
          this.setupInterestDate(KeyDatesEnum.interestEndDate, result);

          this.redemptionDateField.setValue(result.redemptionDateField);
          this.redemptionDatePriority.setValue(result.redemptionDatePriority);
          this.redemptionDateAnchorDate.setValue(
            result.redemptionDateAnchorDate
          );
          this.redemptionDateOffset.setValue(result.redemptionDateOffset);
          this.redemptionDateCalendar.setValue(result.redemptionDateCalendar);
          this.redemptionDateRollConvention.setValue(
            result.redemptionDateRollConvention
          );

          this.initialObservationField.setValue(result.initialObservationField);
          this.initialObservationPriority.setValue(
            result.initialObservationPriority
          );
          this.initialObservationAnchorDate.setValue(
            result.initialObservationAnchorDate
          );
          this.initialObservationOffset.setValue(
            result.initialObservationOffset
          );
          this.initialObservationCalendar.setValue(
            result.initialObservationCalendar
          );
          this.initialObservationRollConvention.setValue(
            result.initialObservationRollConvention
          );

          this.finalObservationField.setValue(result.finalObservationField);
          this.finalObservationPriority.setValue(
            result.finalObservationPriority
          );
          this.finalObservationAnchorDate.setValue(
            result.finalObservationAnchorDate
          );
          this.finalObservationOffset.setValue(result.finalObservationOffset);
          this.finalObservationCalendar.setValue(
            result.finalObservationCalendar
          );
          this.finalObservationRollConvention.setValue(
            result.finalObservationRollConvention
          );

          this.tradeDateField.setValue(result.tradeDateField);
          this.tradeDatePriority.setValue(result.tradeDatePriority);
          this.tradeDateAnchorDate.setValue(result.tradeDateAnchorDate);
          this.tradeDateOffset.setValue(result.tradeDateOffset);
          this.tradeDateCalendar.setValue(result.tradeDateCalendar);
          this.tradeDateRollConvention.setValue(result.tradeDateRollConvention);

          this.productFinalisationDate
            .get('value')
            .setValue(result.productFinalisationDateValue),
            this.productFinalisationDate
              .get('priority')
              .setValue(result.productFinalisationDatePriority),
            this.productFinalisationDate
              .get('anchorDate')
              .setValue(result.productFinalisationDateAnchorDate),
            this.productFinalisationDate
              .get('offset')
              .setValue(result.productFinalisationDateOffset),
            this.productFinalisationDate
              .get('calendar')
              .setValue(result.productFinalisationDateCalendar),
            this.productFinalisationDate
              .get('rollConvention')
              .setValue(result.productFinalisationDateRollConvention);
        }
      },
      (error) => console.log(error)
    );
  }

  setupInterestDate(
    field: KeyDatesEnum.interestStartDate | KeyDatesEnum.interestEndDate,
    result: any
  ): void {
    const {
      [`${field}Date`]: value,
      [`${field}Priority`]: priority,
      [`${field}Calendar`]: calendar,
      [`${field}RollConvention`]: rollConvention,
      [`${field}Offset`]: offset,
      [`${field}From`]: anchorDate,
    } = result;

    this[`${field}Field`].patchValue({
      value,
      priority,
      calendar,
      rollConvention,
      offset,
      anchorDate,
    });
  }

  prepareKeyDatesData(): any {
    return {
      productType: this.form.get(SummaryEnum.productType).value,
      productForm: this.rootFormGroup.form,
      couponType: this.couponTypeControl.value,
      tradingCalendarItemsUse: this.tradingCalendarItemsUse,
      settlementCalendarsItemsUse:
        this.productCaptureService.updateSettlementCalendarItems(
          this.currencyField.value,
          this.currencies,
          this.settlementCalendarItemsClean
        ),
      showProductFinalisationDateRow: this.showProductFinalisationDateRow,

      tenorField: this.tenorField.value,
      frequencyField: this.frequencyField.value,
      tradingCalendars: this.tradingCalendars.value,
      settlementCalendars: this.settlementCalendars.value,
      tradingRollConvention: this.tradingRollConvention.value,
      settlementRollConvention: this.settlementRollConvention.value,
      accrualRollConvention: this.accrualRollConvention.value,

      issueDate: new KeyDateDetail(
        this.issueDateField.value,
        this.issueDatePriority.value,
        this.issueDateAnchorDate.value,
        this.issueDateOffset.value,
        this.issueDateCalendar.value,
        this.issueDateRollConvention.value
      ),

      interestStartDate: new KeyDateDetail(
        this.interestStartDateField.get('value').value,
        this.interestStartDateField.get('priority').value,
        this.interestStartDateField.get('anchorDate').value,
        this.interestStartDateField.get('offset').value,
        this.interestStartDateField.get('calendar').value,
        this.interestStartDateField.get('rollConvention').value
      ),

      interestEndDate: new KeyDateDetail(
        this.interestEndDateField.get('value').value,
        this.interestEndDateField.get('priority').value,
        this.interestEndDateField.get('anchorDate').value,
        this.interestEndDateField.get('offset').value,
        this.interestEndDateField.get('calendar').value,
        this.interestEndDateField.get('rollConvention').value
      ),

      redemptionDate: new KeyDateDetail(
        this.redemptionDateField.value,
        this.redemptionDatePriority.value,
        this.redemptionDateAnchorDate.value,
        this.redemptionDateOffset.value,
        this.redemptionDateCalendar.value,
        this.redemptionDateRollConvention.value
      ),

      initialObservationDate: new KeyDateDetail(
        this.initialObservationField.value,
        this.initialObservationPriority.value,
        this.initialObservationAnchorDate.value,
        this.initialObservationOffset.value,
        this.initialObservationCalendar.value,
        this.initialObservationRollConvention.value
      ),

      finalObservationDate: new KeyDateDetail(
        this.finalObservationField.value,
        this.finalObservationPriority.value,
        this.finalObservationAnchorDate.value,
        this.finalObservationOffset.value,
        this.finalObservationCalendar.value,
        this.finalObservationRollConvention.value
      ),

      tradeDate: new KeyDateDetail(
        this.tradeDateField.value,
        this.tradeDatePriority.value,
        this.tradeDateAnchorDate.value,
        this.tradeDateOffset.value,
        this.tradeDateCalendar.value,
        this.tradeDateRollConvention.value
      ),

      productFinalisationDate: new KeyDateDetail(
        this.productFinalisationDate.get('value').value,
        this.productFinalisationDate.get('priority').value,
        this.productFinalisationDate.get('anchorDate').value,
        this.productFinalisationDate.get('offset').value,
        this.productFinalisationDate.get('calendar').value,
        this.productFinalisationDate.get('rollConvention').value
      ),
    };
  }

  openReferenceModal(): void {
    this.referenceModalService
      .openDialog({
        performanceType: this.performanceType.value,
        settlementType: this.settlementType.value,
        referenceEntities: this.referenceEntitiesArray.value,

        referenceEntityItems: this.referenceEntityItems,
        referenceObligationItems: this.referenceObligationItems,
        productId: this.productId,
      })
      .then(
        (result) => {
          if (result !== 'cancel') {
            this.performanceType.setValue(result.performanceType);
            this.settlementType.setValue(result.settlementType);
            this.referenceEntitiesArray.setValue(result.referenceEntities);

            const referenceEntities = this.referenceEntityItems
              .filter((entity) =>
                result.referenceEntities.find(
                  (refEntity: any) =>
                    refEntity.referenceEntityId === entity.referenceEntityID
                )
              )
              .sort((a, b) => {
                let indexOfA = result.referenceEntities.findIndex(
                  (element) => element.referenceEntityId === a.referenceEntityID
                );
                let indexOfB = result.referenceEntities.findIndex(
                  (element) => element.referenceEntityId === b.referenceEntityID
                );

                return indexOfA - indexOfB;
              });

            this.selectedReferenceEntities$.next(referenceEntities);
            this.referenceEntitiesDisplay.setValue(
              referenceEntities.map((entity) => entity.referenceEntityID)
            );
          }
        },
        (error) => console.log(error)
      );
  }

  private getReferenceOptions(): void {
    forkJoin({
      obligations: this.productCaptureService.getReferenceObligations(),
      entities: this.productCaptureService.getReferenceEntities(),
    }).subscribe(
      (result: any) => {
        this.referenceObligationItems =
          result.obligations as ReferenceObligation[];
        this.referenceEntityItems = result.entities as ReferenceEntity[];

        this.updateSelectedReferenceEntities();
      },
      (error) => {
        console.log(error);
      }
    );
  }

  get couponTypeControl(): FormControl {
    return this.rootFormGroup.form
      .get('payoff')
      .get(PayoffEnum.coupon)
      .get('couponType') as FormControl;
  }

  get selectedReferenceEntities$(): Subject<ReferenceEntity[]> {
    return this.creditProvisionService.selectedReferenceEntities$;
  }

  // Summary
  get productNameField(): FormControl {
    return this.form.get('productName') as FormControl;
  }

  get ISINField(): FormControl {
    return this.form.get('isin') as FormControl;
  }

  get currencyField(): FormControl {
    return this.form.get('currency') as FormControl;
  }

  get tenorField(): FormControl {
    return this.form.get('tenor') as FormControl;
  }

  get frequencyField(): FormControl {
    return this.form.get('frequency') as FormControl;
  }

  get performanceType(): FormControl {
    return this.form.get('performanceType') as FormControl;
  }

  get settlementType(): FormControl {
    return this.form.get('settlementType') as FormControl;
  }

  get tradingRollConvention(): FormControl {
    return this.form.get('tradingRollConvention') as FormControl;
  }

  get settlementRollConvention(): FormControl {
    return this.form.get('settlementRollConvention') as FormControl;
  }

  get accrualRollConvention(): FormControl {
    return this.form.get('accrualRollConvention') as FormControl;
  }

  // Underlying assets
  get underlyingAssetsGroup(): FormGroup {
    return this.form.get('underlyingAssetsGroup') as FormGroup;
  }

  get underlyingAssetsArray(): FormArray {
    return this.underlyingAssetsGroup.get('underlyingAssets') as FormArray;
  }

  // Reference Entities
  get referenceEntitiesGroup(): FormGroup {
    return this.form.get('referenceEntitiesGroup') as FormGroup;
  }

  get referenceEntitiesArray(): FormArray {
    return this.referenceEntitiesGroup.get('referenceEntities') as FormArray;
  }

  // Key dates
  get keyDatesGroup(): FormGroup {
    return this.form.get('keyDates') as FormGroup;
  }

  // >> issueDate
  get issueDateField(): FormControl {
    return this.keyDatesGroup.get('issueDate').get('value') as FormControl;
  }

  get interestStartDateField(): FormGroup {
    return this.keyDatesGroup.get(KeyDatesEnum.interestStartDate) as FormGroup;
  }

  get interestEndDateField(): FormGroup {
    return this.keyDatesGroup.get(KeyDatesEnum.interestEndDate) as FormGroup;
  }

  get issueDatePriority(): FormControl {
    return this.keyDatesGroup.get('issueDate').get('priority') as FormControl;
  }

  get issueDateAnchorDate(): FormControl {
    return this.keyDatesGroup.get('issueDate').get('anchorDate') as FormControl;
  }

  get issueDateOffset(): FormControl {
    return this.keyDatesGroup.get('issueDate').get('offset') as FormControl;
  }

  get issueDateCalendar(): FormControl {
    return this.keyDatesGroup.get('issueDate').get('calendar') as FormControl;
  }

  get issueDateRollConvention(): FormControl {
    return this.keyDatesGroup
      .get('issueDate')
      .get('rollConvention') as FormControl;
  }

  // >> redemptionDate
  get redemptionDateField(): FormControl {
    return this.keyDatesGroup.get('redemptionDate').get('value') as FormControl;
  }

  get redemptionDatePriority(): FormControl {
    return this.keyDatesGroup
      .get('redemptionDate')
      .get('priority') as FormControl;
  }

  get redemptionDateAnchorDate(): FormControl {
    return this.keyDatesGroup
      .get('redemptionDate')
      .get('anchorDate') as FormControl;
  }

  get redemptionDateOffset(): FormControl {
    return this.keyDatesGroup
      .get('redemptionDate')
      .get('offset') as FormControl;
  }

  get redemptionDateCalendar(): FormControl {
    return this.keyDatesGroup
      .get('redemptionDate')
      .get('calendar') as FormControl;
  }

  get redemptionDateRollConvention(): FormControl {
    return this.keyDatesGroup
      .get('redemptionDate')
      .get('rollConvention') as FormControl;
  }

  // >> initialObservationDate
  get initialObservationField(): FormControl {
    return this.keyDatesGroup
      .get('initialObservationDate')
      .get('value') as FormControl;
  }

  get initialObservationPriority(): FormControl {
    return this.keyDatesGroup
      .get('initialObservationDate')
      .get('priority') as FormControl;
  }

  get initialObservationAnchorDate(): FormControl {
    return this.keyDatesGroup
      .get('initialObservationDate')
      .get('anchorDate') as FormControl;
  }

  get initialObservationOffset(): FormControl {
    return this.keyDatesGroup
      .get('initialObservationDate')
      .get('offset') as FormControl;
  }

  get initialObservationCalendar(): FormControl {
    return this.keyDatesGroup
      .get('initialObservationDate')
      .get('calendar') as FormControl;
  }

  get initialObservationRollConvention(): FormControl {
    return this.keyDatesGroup
      .get('initialObservationDate')
      .get('rollConvention') as FormControl;
  }

  // >> finalObservationDate
  get finalObservationField(): FormControl {
    return this.keyDatesGroup
      .get('finalObservationDate')
      .get('value') as FormControl;
  }

  get finalObservationPriority(): FormControl {
    return this.keyDatesGroup
      .get('finalObservationDate')
      .get('priority') as FormControl;
  }

  get finalObservationAnchorDate(): FormControl {
    return this.keyDatesGroup
      .get('finalObservationDate')
      .get('anchorDate') as FormControl;
  }

  get finalObservationOffset(): FormControl {
    return this.keyDatesGroup
      .get('finalObservationDate')
      .get('offset') as FormControl;
  }

  get finalObservationCalendar(): FormControl {
    return this.keyDatesGroup
      .get('finalObservationDate')
      .get('calendar') as FormControl;
  }

  get finalObservationRollConvention(): FormControl {
    return this.keyDatesGroup
      .get('finalObservationDate')
      .get('rollConvention') as FormControl;
  }

  // >> tradeDate
  get tradeDateField(): FormControl {
    return this.keyDatesGroup.get('tradeDate').get('value') as FormControl;
  }

  get tradeDatePriority(): FormControl {
    return this.keyDatesGroup.get('tradeDate').get('priority') as FormControl;
  }

  get tradeDateAnchorDate(): FormControl {
    return this.keyDatesGroup.get('tradeDate').get('anchorDate') as FormControl;
  }

  get tradeDateOffset(): FormControl {
    return this.keyDatesGroup.get('tradeDate').get('offset') as FormControl;
  }

  get tradeDateCalendar(): FormControl {
    return this.keyDatesGroup.get('tradeDate').get('calendar') as FormControl;
  }

  get tradeDateRollConvention(): FormControl {
    return this.keyDatesGroup
      .get('tradeDate')
      .get('rollConvention') as FormControl;
  }

  // productFinalisationDate
  get productFinalisationDate(): FormGroup {
    return this.keyDatesGroup.get('productFinalisationDate') as FormGroup;
  }

  // Other
  get rollAs(): FormControl {
    return this.underlyingAssetsGroup.get('rollAs') as FormControl;
  }

  get tradingCalendars(): FormControl {
    return this.rootFormGroup.form
      .get('summary')
      .get('tradingCalendars') as FormControl;
  }

  get settlementCalendars(): FormControl {
    return this.rootFormGroup.form
      .get('summary')
      .get('settlementCalendars') as FormControl;
  }

  get referenceEntitiesDisplay(): FormControl {
    return this.rootFormGroup.form
      .get('summary')
      .get('referenceEntitiesDisplay') as FormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.dcInputNumberPercentIndicativeFinalService.reset();
    this.destroy$.unsubscribe();
  }

  private setPerformanceAndSettlementDropdownOptions(
    productType: string
  ): void {
    if (productType === ProductTypeEnum.CHOOSER) {
      this.performanceTypeItems = PERFORMANCE_TYPES.filter(
        ({ id }) =>
          id === PERFORMANCE_TYPE_KEYS.BEST_OF ||
          id === PERFORMANCE_TYPE_KEYS.WORST_OF
      );
      this.settlementTypeItems = SETTLEMENT_TYPE_REFERENCE;
    } else if (productType === ProductTypeEnum.CLN) {
      this.performanceTypeItems = PERFORMANCE_TYPES_REFERENCE;
      this.settlementTypeItems = SETTLEMENT_TYPE_REFERENCE;
    } else {
      this.performanceTypeItems = PERFORMANCE_TYPES;
      this.settlementTypeItems = SETTLEMENT_TYPE;
    }
  }

  private filterStatusBySupervisorRole([isSupervisor, nextStateDisplayNames]: [
    boolean,
    string[]
  ]): string[] {
    return isSupervisor
      ? nextStateDisplayNames
      : nextStateDisplayNames.filter((state) => state !== 'Partially Complete');
  }
}
